import { FileSystemEntry, FileSystemPermission } from './types';

export class FileSystemService {
    /**
     * Request user permission to access a directory
     */
    async requestAccess(): Promise<FileSystemDirectoryHandle> {
        try {
            const handle = await window.showDirectoryPicker({
                mode: 'readwrite'
            });
            return handle;
        } catch (error: any) {
            if (error.name === 'AbortError') {
                throw new Error('User cancelled directory selection');
            }
            throw error;
        }
    }

    /**
     * Verify if we still have permission to access the handle
     */
    async verifyPermission(handle: FileSystemHandle, mode: FileSystemPermission = 'readwrite'): Promise<boolean> {
        const options = { mode };
        // Check if permission is already granted. If so, return true.
        if ((await handle.queryPermission(options)) === 'granted') {
            return true;
        }
        // Request permission to the file, if the user grants permission, return true.
        if ((await handle.requestPermission(options)) === 'granted') {
            return true;
        }
        // The user did not grant permission, return false.
        return false;
    }

    /**
     * Read text content from a file relative to a directory handle
     */
    async readFile(directoryHandle: FileSystemDirectoryHandle, path: string): Promise<string> {
        try {
            const fileHandle = await this.getFileHandle(directoryHandle, path);
            const file = await fileHandle.getFile();
            return await file.text();
        } catch (error) {
            console.error(`Error reading file at ${path}:`, error);
            throw error;
        }
    }

    /**
     * Write text content to a file relative to a directory handle
     */
    async writeFile(directoryHandle: FileSystemDirectoryHandle, path: string, content: string): Promise<void> {
        try {
            const fileHandle = await this.getFileHandle(directoryHandle, path, true);
            const writable = await fileHandle.createWritable();
            await writable.write(content);
            await writable.close();
        } catch (error) {
            console.error(`Error writing file at ${path}:`, error);
            throw error;
        }
    }

    /**
     * Create a directory (recursive if needed)
     */
    async createDirectory(directoryHandle: FileSystemDirectoryHandle, name: string): Promise<FileSystemDirectoryHandle> {
        return await directoryHandle.getDirectoryHandle(name, { create: true });
    }

    /**
     * List entries in a directory
     */
    async listDirectory(directoryHandle: FileSystemDirectoryHandle): Promise<FileSystemEntry[]> {
        const entries: FileSystemEntry[] = [];
        for await (const [name, handle] of directoryHandle.entries()) {
            entries.push({
                name,
                kind: handle.kind,
                handle
            });
        }
        return entries;
    }

    /**
     * Check if a file or directory exists
     */
    async exists(directoryHandle: FileSystemDirectoryHandle, path: string): Promise<boolean> {
        try {
            const parts = path.split('/').filter(p => p.length > 0);
            let current = directoryHandle;

            for (let i = 0; i < parts.length - 1; i++) {
                current = await current.getDirectoryHandle(parts[i]);
            }

            const lastPart = parts[parts.length - 1];
            try {
                await current.getFileHandle(lastPart);
                return true;
            } catch {
                try {
                    await current.getDirectoryHandle(lastPart);
                    return true;
                } catch {
                    return false;
                }
            }
        } catch {
            return false;
        }
    }

    /**
     * Helper to resolve a file handle from a path string
     */
    private async getFileHandle(
        directoryHandle: FileSystemDirectoryHandle,
        path: string,
        create = false
    ): Promise<FileSystemFileHandle> {
        const parts = path.split('/').filter(p => p.length > 0);
        let current = directoryHandle;

        // Navigate through directories
        for (let i = 0; i < parts.length - 1; i++) {
            current = await current.getDirectoryHandle(parts[i], { create });
        }

        const fileName = parts[parts.length - 1];
        return await current.getFileHandle(fileName, { create });
    }
}

export const fileSystemService = new FileSystemService();
